import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {confirmDelete} from '../../../../utils/ecxSwal';
import SubHeader from '../../../layout/components/SubHeader';
import TimeDifference from '../../../common/TimeDifference';
import {useEyeconxStore} from '../../../../store/eyeconxStore';
import {
    clearNotificationsForClinic,
    deleteNotificationById,
    loadNotifications,
} from './notificationStorage';
import {
    formatNotificationTypeLabel,
    formatPhoneNumber,
    getNotificationTypeBadgeClass,
} from './notificationUtils';
import './Notifications.css';

function NotificationMetaRow({icon, label, value}) {
    if (!value) {
        return null;
    }
    return (
        <div className="sms-notification-meta-row">
            <span className="sms-notification-meta-label">
                <i className={`bi ${icon} me-1`}></i>
                {label}
            </span>
            <span className="sms-notification-meta-value">{value}</span>
        </div>
    );
}

function Notifications() {
    const [notifications, setNotifications] = useState([]);
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const resetNotifications = useEyeconxStore((state) => state.resetNotifications);
    const navigate = useNavigate();

    const refreshList = useCallback(() => {
        setNotifications(loadNotifications(clinicId));
    }, [clinicId]);

    useEffect(() => {
        refreshList();
        resetNotifications();
    }, [clinicId, refreshList, resetNotifications]);

    const handleDelete = async (notification) => {
        const result = await confirmDelete({
            title: 'Delete notification?',
            itemName: 'this notification',
            description: 'This cannot be undone.',
        });

        if (!result.isConfirmed) {
            return;
        }

        deleteNotificationById(notification.id);
        refreshList();
        toast.success('Notification removed', {position: 'bottom-right'});
    };

    const handleClearAll = async () => {
        if (notifications.length === 0) {
            return;
        }

        const count = notifications.length;
        const result = await confirmDelete({
            title: 'Clear all notifications?',
            itemName: count === 1 ? 'this notification' : `all ${count} notifications`,
            description: 'They will be removed from this device.',
            confirmText: 'Clear all',
        });

        if (!result.isConfirmed) {
            return;
        }

        clearNotificationsForClinic(clinicId);
        refreshList();
        toast.success('All notifications cleared', {position: 'bottom-right'});
    };

    const handleQuickReply = (notification) => {
        if (!notification.customer) {
            toast.info('No contact number linked to this notification', {position: 'bottom-right'});
            return;
        }
        navigate('/sms-messages', {state: {customer: notification.customer}});
    };

    return (
        <>
            <SubHeader
                title="Past Notifications"
                description="SMS alerts and updates received for your clinic"
                icon="bi-bell-fill"
                iconClass="text-warning"
                containerClass="container-fluid px-4"
            >
                <div className="d-flex align-items-center gap-2 flex-wrap sms-help-subheader-action">
                    <Link
                        to="/sms-clinic-settings"
                        className="btn btn-light btn-sm rounded-pill sms-help-back-link"
                    >
                        <i className="bi bi-arrow-left me-1"/>
                        Back to Settings
                    </Link>
                    {notifications.length > 0 && (
                        <button
                            type="button"
                            className="btn btn-light-danger btn-sm rounded-pill"
                            onClick={handleClearAll}
                        >
                            <i className="bi bi-trash3 me-1"></i>
                            Clear all
                        </button>
                    )}
                </div>
            </SubHeader>

            <div className="container-fluid px-4 py-3">
                <div className="card shadow-sm border-0 sms-notifications-card">
                    <div className="card-header bg-white border-bottom py-3">
                        <div className="d-flex align-items-center justify-content-between">
                            <span className="text-muted small fw-normal">
                                <i className="bi bi-bell me-2"></i>
                                {notifications.length === 0
                                    ? 'No notifications'
                                    : `${notifications.length} notification${notifications.length === 1 ? '' : 's'}`}
                            </span>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        {notifications.length === 0 ? (
                            <div className="d-flex flex-column align-items-center justify-content-center text-center py-5 px-4">
                                <div className="bg-light rounded-circle d-flex align-items-center justify-content-center sms-notification-empty-icon mb-4">
                                    <i className="bi bi-bell-slash text-muted" style={{fontSize: '2rem'}}></i>
                                </div>
                                <h5 className="text-dark fw-normal mb-2">No notifications yet</h5>
                                <p className="text-muted mb-0 small">
                                    New SMS alerts will appear here when patients message your clinic.
                                </p>
                            </div>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {notifications.map((notification) => {
                                    const typeLabel = formatNotificationTypeLabel(notification.notificationType);
                                    const typeBadgeClass = getNotificationTypeBadgeClass(notification.notificationType);
                                    const customerPhone = formatPhoneNumber(notification.customer);
                                    const businessPhone = formatPhoneNumber(notification.business);

                                    return (
                                        <li
                                            key={notification.id}
                                            className="list-group-item sms-notification-item border-0 border-bottom px-4 py-3"
                                        >
                                            <div className="d-flex gap-3 align-items-start">
                                                <div className="bg-light-primary rounded-circle d-flex align-items-center justify-content-center sms-notification-icon">
                                                    <i className="bi bi-chat-dots text-primary"></i>
                                                </div>

                                                <div className="flex-grow-1 min-w-0">
                                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                                        <span className={`badge rounded-pill ${typeBadgeClass}`}>
                                                            {typeLabel}
                                                        </span>
                                                        <span className="text-muted small">
                                                            <i className="bi bi-clock me-1"></i>
                                                            <TimeDifference dateString={notification.timestamp}/>
                                                            <span className="mx-1">·</span>
                                                            {new Date(notification.timestamp).toLocaleString()}
                                                        </span>
                                                    </div>

                                                    <p className="mb-2 text-dark fw-normal">
                                                        {notification.message || 'New notification'}
                                                    </p>

                                                    <div className="sms-notification-meta">
                                                        <NotificationMetaRow
                                                            icon="bi-telephone-outbound"
                                                            label="From"
                                                            value={customerPhone}
                                                        />
                                                        <NotificationMetaRow
                                                            icon="bi-telephone-inbound"
                                                            label="Clinic line"
                                                            value={businessPhone}
                                                        />
                                                        {notification.clinicId && (
                                                            <NotificationMetaRow
                                                                icon="bi-building"
                                                                label="Clinic ID"
                                                                value={notification.clinicId}
                                                            />
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="d-flex flex-shrink-0 gap-2">
                                                    {notification.customer && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-light-primary btn-sm rounded-pill"
                                                            onClick={() => handleQuickReply(notification)}
                                                            title="Open conversation"
                                                        >
                                                            <i className="bi bi-chat-square-text me-1"></i>
                                                            Reply
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-light-danger btn-sm rounded-pill"
                                                        onClick={() => handleDelete(notification)}
                                                        title="Delete notification"
                                                    >
                                                        <i className="bi bi-trash3"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default Notifications;
